import { Form } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import FixMeLater from "../../../shared/types/fixMeLater.type";
import { KeyFobList } from "../../../shared/interface/MasterManagement/keyFobMaster.interface";
import { RootState } from "../../../appRedux/store";
import useValue from "../../../hooks/useValue";
import * as commonAPI from "../../../services/commonServices";
import { CustomNotification } from "../../../util/CustomNotification";
import * as advanceBookingRequestService from "../../../services/advanceBookingRequestService";

import type { FormattedABRRequest, RaisedBookingRequestFormData } from "../../../shared/interface/advanceBookingRequest";

type useKeyFobAllocationModalProps = {
  handleRefreshList: () => void;
  data: FormattedABRRequest | null;
  handleOnClose: () => void | undefined;
  isModalVisible: boolean;
};

export const useKeyFobAllocationModal = ({ handleRefreshList, data, isModalVisible, handleOnClose }: useKeyFobAllocationModalProps) => {
  /* FORM & REFS */
  const [form] = Form.useForm();

  /* STATE */
  const [keyFobList, setKeyFobList] = useState<KeyFobList[]>([]);
  const [mobileNumber, setMobileNumber] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<FixMeLater>(null);
  const [isDisabled, setIsDisabled] = useState(false);

  /* CUSTOM HOOKS */
  const { value: loading, handleDisableValue: disableLoading, handleEnableValue: enableLoading } = useValue(true);
  const { value: saving, handleDisableValue: disableSaving, handleEnableValue: enableSaving } = useValue(false);
  const { masterFilters } = useSelector(({ common }: RootState) => common);
  const { customerId, locationId } = masterFilters ?? {};

  /* HANDLERS */
  const resetUserDetailsField = () => {
    form.setFieldsValue({ first_name: null, last_name: null });
    setUserDetails(null);
  };

  const handleChangeNumber = (number: string) => {
    resetUserDetailsField();
    setMobileNumber(`+91${number}`);
  };

  const fetchUserDetails = async (mobileNumber: string | number) => {
    try {
      const response = await commonAPI.verifyMobileUserAndGetDetails(`+91${mobileNumber}`);
      const { first_name, last_name } = response?.data ?? {};

      form.setFieldsValue({ first_name, last_name });
      setUserDetails(response?.data);
    } catch (error) {
      resetUserDetailsField();
      CustomNotification.showErrorMessage(error);
    }
  };

  const verifyMobileNumber = async () => {
    if (!mobileNumber) return;
    await fetchUserDetails(mobileNumber);
  };

  const handleFinish = async (formData: RaisedBookingRequestFormData) => {
    try {
      enableSaving();

      const dataToSend = {
        ...formData,
        customer_id: customerId!,
        customer_location_id: locationId!
      };

      const response = await advanceBookingRequestService.raiseParkingBookingRequestAPI(data?.advance_booking_request_id!, dataToSend);

      CustomNotification.showSuccessMessage(response?.message);
      handleRefreshList();
      handleOnClose();
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      disableSaving();
    }
  };

  const handleRetrievalRequestClose = () => {
    handleRefreshList();
    handleOnClose();
  };

  /* FETCH DATA */
  const fetchKeyFobList = async (vehicleTypeId: number, isKeyFobAllocationExist: boolean) => {
    try {
      if (!vehicleTypeId) return;
      enableLoading();
      let response;

      if (isKeyFobAllocationExist) {
        response = await commonAPI.getKeyFobListByCustomerAndLocationId(customerId!, locationId!);
      } else {
        response = await commonAPI.getUnallocatedKeyFobListByCustomerAndLocationId(customerId!, locationId!, vehicleTypeId);
      }

      const formattedResponse = response?.data?.map((item) => ({
        ...item,
        keyFob_vehicle_dimension: `${item?.key_fob_token ?? ""} - ${item?.vehicleType?.vehicle_type_name ?? ""} - ${item?.vehicleType?.dimension ?? ""}`
      }));

      setKeyFobList(formattedResponse);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      disableLoading();
    }
  };

  const fetchAbrDetails = async (abrId: number) => {
    try {
      if (!abrId) return;
      const response = await advanceBookingRequestService.getAbrDetailsAPI(abrId);

      const formData = response?.data;
      form.setFieldsValue({
        key_fob_id: formData?.keyFobAllocation?.key_fob_id,
        vehicle_number: formData?.keyFobAllocation?.vehicle_number,
        booking_id: formData?.booking_id
      });
      setMobileNumber(formData?.keyFobAllocation?.mobileUser?.mobile_number);

      setKeyFobList((prev: any) => [
        ...prev,
        {
          id: formData?.keyFobAllocation?.keyFob?.id,
          key_fob_token: formData?.keyFobAllocation?.keyFob?.key_fob_token,
          vehicleType: {
            id: formData?.vehicleType?.id,
            vehicle_type_name: formData?.vehicleType?.vehicle_type_name,
            dimension: formData?.vehicleType?.dimension
          },
          keyFob_vehicle_dimension: `${formData?.keyFobAllocation?.keyFob?.key_fob_token ?? ""} - ${formData?.vehicleType?.vehicle_type_name ?? ""} - ${formData?.vehicleType?.dimension ?? ""}`
        }
      ]);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  /* EFFECTS */
  useEffect(() => {
    const fetchDataAndUpdateFormValue = async () => {
      try {
        if (!data) return;

        enableLoading();
        setIsDisabled(data?.isKeyFobAllocationExist);
        const [first_name, last_name] = data?.username?.split(" ");
        const mobile_number = data?.mobile_number?.replace(/^\+91/, "");

        await fetchUserDetails(mobile_number);
        await fetchAbrDetails(data?.advance_booking_request_id);
        await fetchKeyFobList(data?.vehicle_type_id, data?.isKeyFobAllocationExist);

        form.setFieldsValue({
          advance_booking_request_id: data?.advance_booking_request_id,
          booking_id: data?.booking_id,
          first_name,
          last_name,
          mobile_number
        });
        setMobileNumber(mobile_number);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        disableLoading();
      }
    };

    if (isModalVisible) fetchDataAndUpdateFormValue();
  }, [isModalVisible]);

  return {
    userDetails,
    keyFobList,
    form,
    verifyMobileNumber,
    mobileNumber,
    handleChangeNumber,
    handleFinish,
    loading,
    saving,
    handleRetrievalRequestClose,
    isDisabled
  };
};
